import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@connectrpc/connect-query'
import { Trans } from '@lingui/react/macro'

import { Form } from '@jotta/form/Form'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import { useResetAfterTimeout } from '@jotta/grpc-connect-client/useResetAfterTimeout'
import { approveLogin as approveLoginDescriptor } from '@jotta/grpc-connect-openapi/customerQuery'
import { Button } from '@jotta/ui/Button'
import { CodeInput } from '@jotta/ui/CodeInput'
import { DeprecatedCard } from '@jotta/ui/DeprecatedCard'
import { Headline3 } from '@jotta/ui/Headline'
import { Label } from '@jotta/ui/Label'

import appleTv from './appletv.png'

export function DeviceLogin({ code }: { code?: string }) {
  const [loginCode, setLoginCode] = useState('')
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  const approveLoginMutation = useMutation(approveLoginDescriptor, {
    throwOnError: false,
    transport: authTransport,
    onSuccess: () => {
      setLoginCode('')
    },
  })

  useResetAfterTimeout({
    mutation: approveLoginMutation,
    timeout: 3000,
    errorTimeout: 0,
  })

  const {
    mutate: approveLogin,
    isSuccess,
    isPending,
    isError,
    reset,
  } = approveLoginMutation

  useEffect(() => {
    if (code) {
      setLoginCode(code)
    }
  }, [code])

  return (
    <div className="flex flex-col items-center gap-16 p-8 sm:p-16">
      <img className="h-8 md:h-24" src={appleTv} />
      <Form
        className="flex flex-col items-center gap-4 px-12 py-4"
        onSubmit={() => {
          approveLogin({
            loginCode,
          })
        }}
      >
        <Label>
          <Headline3>
            <Trans>To sign in, enter your code below</Trans>
          </Headline3>
          <CodeInput
            className="py-6"
            autoFocus
            codeLength={6}
            code={loginCode}
            onCodeChange={code => {
              setLoginCode(code)
              reset()
            }}
            onCodeComplete={() => submitButtonRef.current?.focus()}
          />
        </Label>
        <Button
          ref={submitButtonRef}
          className="btn btn-primary"
          type="submit"
          loading={isPending}
        >
          <Trans>Send code</Trans>
        </Button>

        <DeprecatedCard
          hidden={!isSuccess}
          variant="cards.successOutline"
          className="w-full"
        >
          <Trans>Success</Trans>
        </DeprecatedCard>

        <DeprecatedCard
          hidden={!isError}
          variant="cards.danger"
          className="w-full"
        >
          <Trans>Incorrect code. Type in the code again!</Trans>
        </DeprecatedCard>
      </Form>
    </div>
  )
}
